import type { I18nProvider } from "@refinedev/core";

import { messages, type Locale } from "./messages";

// jmix-exit E3a.3: Refine I18nProvider over the flat message registry (messages.ts). Ported from
// backoffice's i18nProvider — dependency-free, nl/en, with a persisted-override → browser → default
// resolution chain. The platform foster portal is Dutch-first, so the default locale is `nl` (the en
// bundle is the fallback for any missing key).

export const SUPPORTED_LOCALES = ["nl", "en"] as const;
const DEFAULT_LOCALE: Locale = "nl";
const STORAGE_KEY = "platform.locale";

function isSupported(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function resolveInitialLocale(): Locale {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
  if (stored && isSupported(stored)) {
    return stored;
  }
  const candidates = globalThis.navigator?.languages ?? [globalThis.navigator?.language ?? ""];
  for (const tag of candidates) {
    const base = tag.toLowerCase().split("-")[0];
    if (isSupported(base)) {
      return base;
    }
  }
  return DEFAULT_LOCALE;
}

let currentLocale: Locale = resolveInitialLocale();

export const i18nProvider: I18nProvider = {
  translate: (key: string, _options?: unknown, defaultMessage?: string): string => {
    const value = messages[currentLocale]?.[key] ?? messages[DEFAULT_LOCALE]?.[key];
    if (value === undefined) {
      return defaultMessage ?? key;
    }
    return value;
  },
  getLocale: () => currentLocale,
  changeLocale: (lang: string) => {
    if (isSupported(lang)) {
      currentLocale = lang;
      globalThis.localStorage?.setItem(STORAGE_KEY, lang);
    }
    return Promise.resolve();
  },
};
